<template>
  <PageTitle pageTitle="Detalhes de Pedido" mainTitle="Pedidos" />

  <v-row class="justify-content-center">
    <v-col cols="12" lg="4" md="12">
      <TrackOrder :is-loading="isLoadingOrderInfo" :order="order" />
    </v-col>

    <v-col cols="12" lg="8" md="12">
      <v-alert
        v-if="isDifferentDay"
        color="red-lighten-1"
        type="warning"
        class="text-start mb-10"
      >
        Pedido agendado para
        <strong>
          {{
            new Date(order.dateReference).toLocaleDateString("pt-BR", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          }} </strong
        >.
      </v-alert>

      <OrderInformation :is-loading="isLoadingOrderInfo" :user="order.user" />

      <v-form>
        <v-row>
          <v-col cols="12" lg="12" md="12">
            <v-card class="create-project-card pingo-card">
              <div class="v-card-header border-bottom">
                <div class="d-flex flex-column align-items-start">
                  <h5 class="mb-0">Informações do Pedido</h5>

                  <span class="gray-color">
                    Pedido criado em
                    {{
                      new Intl.DateTimeFormat("pt-BR", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }).format(new Date(order.createdAt))
                    }}
                  </span>
                </div>

                <div>
                  <v-dialog width="auto" scrollable v-model="isDialogOpen">
                    <template v-slot:activator="{ props: activatorProps }">
                      <div
                        v-if="user.type == 'ADMIN' || user.type == 'ATTENDANT'"
                        class="d-flex ga-2"
                      >
                        <v-btn
                          class="text-capitalize"
                          color="blue-darken-3"
                          variant="tonal"
                          v-bind="activatorProps"
                        >
                          Atualizar status
                        </v-btn>

                        <PrinterComponent :order="order" />
                      </div>
                    </template>

                    <template v-slot:default="">
                      <v-card class="py-0" title="Atualizar Status">
                        <v-divider></v-divider>

                        <v-card-text class="px-4">
                          Tem certeza que deseja atualizar o status do pedido
                          para o próximo estágio?
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions>
                          <v-btn
                            class="text-capitalize"
                            @click="isDialogOpen = false"
                          >
                            Fechar
                          </v-btn>

                          <v-spacer></v-spacer>

                          <v-btn
                            class="text-capitalize"
                            color="blue-darken-3"
                            variant="tonal"
                            @click="updateOrderStatus"
                          >
                            Atualizar
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </template>
                  </v-dialog>
                </div>
              </div>

              <v-data-table
                class="table"
                :headers="newOrderColumns"
                :items="order.items"
                hide-default-footer
              >
                <template v-slot:[`item.name`]="{ item }">
                  <a class="d-flex align-items-center product-info">
                    <v-img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3f/Placeholder_view_vector.svg/1022px-Placeholder_view_vector.svg.png?20220519031949"
                      class="border-radius me-12"
                      width="60"
                      alt="product"
                    />
                    <div class="title">
                      <h5 class="fw-semibold mb-1">
                        {{ item.item?.name ?? "Desconhecido" }}
                      </h5>

                      <span class="d-block muted-color fw-medium">
                        Descrição:
                        <span class="gray-color">
                          {{ item.item?.description ?? "Não informado" }}
                        </span>
                      </span>
                    </div>
                  </a>
                </template>

                <template v-slot:[`item.price`]="{ item }">
                  {{
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(parseFloat(item.unitPrice) ?? 0)
                  }}
                </template>

                <template v-slot:[`item.observation`]="{ item }">
                  <span
                    v-if="item.observations"
                    class="text-capitalize badge rounded-pill text-soft-danger"
                  >
                    {{ item.observations }}
                  </span>

                  <span
                    v-if="!item.observations"
                    class="text-capitalize badge rounded-pill text-soft-warning"
                  >
                    N/A
                  </span>
                </template>

                <template v-slot:[`item.total`]="{ item }">
                  {{
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format((parseFloat(item.unitPrice) ?? 0) * item.quantity)
                  }}
                </template>

                <template v-slot:[`item.actions`]="{}"> - </template>

                <template v-slot:no-data> Não há nenhum produto </template>

                <template v-slot:loading>
                  <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
                </template>
              </v-data-table>

              <div class="table-responsive">
                <table class="table">
                  <tbody>
                    <!-- Summary -->
                    <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="text-end fw-semibold gray-color">
                        Sub Total:
                      </td>
                      <td class="fw-semibold text-end">
                        {{
                          new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(subtotal)
                        }}
                      </td>
                    </tr>

                    <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="text-end fw-semibold gray-color">Desconto:</td>
                      <td class="fw-semibold text-end">
                        {{
                          new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(discount)
                        }}
                      </td>
                    </tr>

                    <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="text-end fw-semibold gray-color">
                        Taxa de Entrega:
                      </td>
                      <td class="fw-semibold text-end">
                        {{
                          new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(deliveryFee)
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="text-end fw-semibold">Total (BRL):</td>
                      <td class="fw-semibold text-end">
                        {{
                          new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(total)
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, Ref, ref } from "vue";

import PageTitle from "@/components/Common/PageTitle.vue";

import { useOrder } from "@/composables/useOrder";
import { useRoute, useRouter } from "vue-router";

import TrackOrder from "@/components/eCommerce/OrderDetails/TrackOrder.vue";

import { OrderDetailsResponse } from "@/types/response/orderDetails";

import PrinterComponent from "@/components/Forms/AdvancedElements/Print/PrinterComponent.vue";
import OrderInformation from "@/components/eCommerce/OrderDetails/OrderInformation.vue";
import { useAuthStore } from "@/stores/auth.store";

export default defineComponent({
  name: "OrderDetailsPage",
  components: {
    PageTitle,
    TrackOrder,
    PrinterComponent,
    OrderInformation,
  },
  setup() {
    const authStore = useAuthStore();

    const route = useRoute();
    const router = useRouter();

    const { newOrderColumns, order, getOrderById, updateOrder } = useOrder();

    const user: Ref = ref(authStore.getUser);

    const isDialogOpen = ref(false);

    const allStatuses: OrderDetailsResponse["status"][] = [
      "pending",
      "confirmed",
      "shipped",
      "delivered",
      "cancelled",
    ];

    // Valores adicionais para desconto e taxa de entrega
    const discount = ref(0.0);
    const deliveryFee = ref(0.0);

    // Computed para calcular o subtotal, total, desconto e taxa de entrega
    const subtotal = computed(() => {
      return order.value.items.reduce((sum, item) => {
        return sum + (parseFloat(item.unitPrice) ?? 0) * item.quantity;
      }, 0);
    });

    const total = computed(() => {
      return subtotal.value - discount.value + deliveryFee.value;
    });

    const isDifferentDay = computed(() => {
      console.log(order.value.dateReference);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const orderDate = new Date(order.value.dateReference);

      return today.getTime() !== orderDate.getTime();
    });

    const isLoadingOrderInfo = ref(false);

    const fetchOrderData = async (order_id: number) => {
      try {
        isLoadingOrderInfo.value = true;

        getOrderById(order_id).then(() => {
          isLoadingOrderInfo.value = false;
        });
      } catch (error) {
        console.error("Erro ao buscar detalhes de pedido:", error);
      }
    };

    const updateOrderStatus = async () => {
      if (!order.value) return;

      const currentStatus = order.value
        .status as OrderDetailsResponse["status"];
      const nextStatusIndex = allStatuses.indexOf(currentStatus) + 1;

      const isTransitionAllowed = (
        current: OrderDetailsResponse["status"],
        next: OrderDetailsResponse["status"]
      ): boolean => {
        if (current === "pending" && next === "delivered") return false;
        if (next === "delivered" && current !== "shipped") return false;

        switch (current) {
          case "pending":
            return ["confirmed", "shipped", "delivered", "cancelled"].includes(
              next
            );
          case "confirmed":
            return ["shipped", "delivered", "cancelled"].includes(next);
          case "shipped":
            return ["delivered", "cancelled"].includes(next);
          case "delivered":
            return false;
          default:
            return false;
        }
      };

      if (nextStatusIndex < allStatuses.length) {
        const nextStatus = allStatuses[nextStatusIndex];

        if (!isTransitionAllowed(currentStatus, nextStatus)) {
          console.error(
            `Mudança de status de '${currentStatus}' para '${nextStatus}' não é permitida.`
          );
          return;
        }

        order.value.status = nextStatus;

        const updatedOrderPayload = { status: order.value.status };
        const response = await updateOrder(
          order.value.orderId,
          updatedOrderPayload
        );

        if (response) {
          router.go(-1);
          isDialogOpen.value = false;
        } else {
          console.error("Erro ao atualizar o status do pedido na API.");
        }
      } else {
        console.error("Não há mais status para transitar.");
      }
    };

    onMounted(() => {
      const orderId = parseInt(route.params.id.toString());
      fetchOrderData(orderId);
    });

    return {
      user,
      isLoadingOrderInfo,
      newOrderColumns,
      order,
      discount,
      deliveryFee,
      subtotal,
      total,
      isDifferentDay,
      isDialogOpen,
      updateOrderStatus,
    };
  },
});
</script>

<style lang="scss" scoped>
.pingo-form-group {
  margin-bottom: 0px;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
  }
  &::-webkit-scrollbar:vertical {
    width: 10px;
  }
  &::-webkit-scrollbar:horizontal {
    height: 10px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    border: 2px solid var(--whiteColor);
    background-color: rgba(0, 0, 0, 0.2);
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: var(--whiteColor);
  }
}
table,
.table {
  width: 100%;
  border-collapse: collapse;

  thead {
    tr {
      th {
        background-color: #f7faff;
        vertical-align: middle;
        white-space: nowrap;
        text-align: center;
        position: relative;
        padding: 18px 20px;

        &::after {
          top: 50%;
          right: 0;
          width: 1px;
          content: "";
          height: 17px;
          position: absolute;
          background-color: #e7ebf5;
          transform: translateY(-50%);
        }
        &:last-child {
          &::after {
            display: none;
          }
        }
      }
    }
  }
  tbody {
    tr {
      td {
        text-align: center;
        padding: 18px 20px;
        white-space: nowrap;
        border-bottom-color: #f7faff;
        border-bottom: 1px solid #f7faff;

        .product-info {
          padding-right: 25px;

          h5 {
            font-size: 15px;
          }
          span {
            font-size: 14px;
          }
        }
        .rating {
          i {
            line-height: 1;
            color: #ffbc2b;
            margin: {
              left: 2px;
              right: 2px;
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 767px) {
  table,
  .table {
    thead {
      tr {
        th {
          padding: 15px;
        }
      }
    }
    tbody {
      tr {
        td {
          padding: 15px;

          .product-info {
            padding-right: 50px;
          }
        }
      }
    }
  }
}

@media only screen and (min-width: 992px) and (max-width: 1199px) {
  table,
  .table {
    thead {
      tr {
        th {
          padding: 15px 20px;
        }
      }
    }
    tbody {
      tr {
        td {
          padding: 15px 20px;
        }
      }
    }
  }
}

.box {
  h5 {
    font-size: 16px;
  }
  .list {
    list-style-type: none;

    li {
      margin-bottom: 13px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

@media only screen and (max-width: 767px) {
  .box {
    h5 {
      font-size: 15px;
    }
  }
}
</style>
